import { call } from '@advanza/api'
import { Button, Icon, Loading } from '@advanza/ui'
import { DIALOG_BACKDROP_FADE } from '@trustoo/components/modal/constants/dialogPresets'
import Dialog from '@trustoo/components/modal/Dialog'
import TroyCaseActions from 'components/billing/TroyCaseActions'
import { useAppDispatch } from 'hooks/hooks'
import { formatMoney } from 'misc/money'
import { ReactNode, useState } from 'react'

interface Props {
    providerId: number
}

interface PlannedInvoice {
    invoiceId: number
    invoiceNr: string
    outstanding: number | null
    total: number | null
    eligible: boolean
    reason: string
    troyCaseId: string | null
    sentToTroy: string | null
    troyCollectionStopped: string | null
    writtenOff: boolean
    handedOver?: boolean
    dunningEvents?: number
    dunningError?: string
    error?: string
}

type Tone = 'primary' | 'success' | 'danger' | 'warning' | 'muted'

const TONE_COLORS: Record<Tone, string> = {
    primary: '#10185a',
    success: '#1e7a4b',
    danger: '#c0392b',
    warning: '#8a6100',
    muted: '#6b7280',
}

const BADGE_CLASSES: Record<Tone, string> = {
    primary: 'bg-[#eef0fa] text-[#10185a]',
    success: 'bg-[#e8f6ef] text-[#1e7a4b]',
    danger: 'bg-[#fdecea] text-[#c0392b]',
    warning: 'bg-[#fff6e5] text-[#8a6100]',
    muted: 'bg-[#f3f4f6] text-[#6b7280]',
}

const CALLOUT_CLASSES: Record<Tone, string> = {
    primary: 'bg-[#eef0fa] text-[#10185a]',
    success: 'bg-[#e8f6ef] text-[#1e7a4b]',
    danger: 'bg-[#fdecea] text-[#c0392b]',
    warning: 'bg-[#fff6e5] text-[#8a6100]',
    muted: 'bg-[#f9fafb] text-[#6b7280]',
}

interface Status {
    label: string
    tone: Tone
    icon: string
}

const STATUSES: Record<string, Status> = {
    eligible: { label: 'Will be sent', tone: 'primary', icon: 'arrow_forward' },
    handed_over: { label: 'Sent to Troy', tone: 'success', icon: 'check_circle' },
    already_sent: { label: 'Already at Troy', tone: 'muted', icon: 'gavel' },
    debtor_case_stopped: {
        label: "Blocked: this provider's Troy case is stopped",
        tone: 'warning',
        icon: 'block',
    },
    collection_stopped: {
        label: 'Collection stopped at Troy',
        tone: 'muted',
        icon: 'pause_circle',
    },
    country_unsupported: { label: 'Country not covered by Troy', tone: 'muted', icon: 'block' },
    country_not_enabled: {
        label: 'Troy is not enabled for this country yet',
        tone: 'muted',
        icon: 'block',
    },
    nothing_outstanding: { label: 'Nothing left to collect', tone: 'muted', icon: 'done_all' },
    no_payment_info: { label: 'No payment info for this provider', tone: 'muted', icon: 'help' },
    unknown_balance: {
        label: 'Could not read the balance from Payt',
        tone: 'danger',
        icon: 'sync_problem',
    },
    below_minimum: { label: 'Below the minimum amount', tone: 'muted', icon: 'remove_circle' },
}

const statusOf = (invoice: PlannedInvoice): Status => {
    if (invoice.error) {
        return { label: invoice.error, tone: 'danger', icon: 'error' }
    }

    return STATUSES[invoice.reason] ?? { label: invoice.reason, tone: 'muted', icon: 'help' }
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const formatAmount = (outstanding: number | null) =>
    outstanding === null ? '—' : formatMoney(outstanding)

const Badge = ({ tone, icon, children }: { tone: Tone; icon?: string; children: ReactNode }) => (
    <span
        className={`inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[12px] font-semibold ${BADGE_CLASSES[tone]}`}>
        {icon && <Icon name={icon} fontSize={14} className="inherit" />}
        {children}
    </span>
)

const Callout = ({ tone, icon, children }: { tone: Tone; icon: string; children: ReactNode }) => (
    <div
        className={`mb-4 flex items-start gap-2 rounded-[6px] px-3 py-2 text-[13px] ${CALLOUT_CLASSES[tone]}`}>
        <Icon name={icon} fontSize={18} className="inherit" />
        <span>{children}</span>
    </div>
)

const InvoiceRow = ({
    invoice,
    showCase = true,
}: {
    invoice: PlannedInvoice
    showCase?: boolean
}) => {
    const status = statusOf(invoice)

    return (
        <li className="flex items-start gap-3 border-b border-[#f0f1f3] px-3 py-2 last:border-b-0">
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{invoice.invoiceNr}</span>
                    {invoice.writtenOff && <Badge tone="warning">written off</Badge>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge tone={status.tone} icon={status.icon}>
                        {status.label}
                    </Badge>
                    {showCase && invoice.troyCaseId && (
                        <span className="text-[12px] text-[#6b7280]">
                            case {invoice.troyCaseId}
                        </span>
                    )}
                    {invoice.dunningEvents !== undefined && invoice.dunningEvents > 0 && (
                        <span className="text-[12px] text-[#6b7280]">
                            {plural(invoice.dunningEvents, 'dunning event')} sent
                        </span>
                    )}
                </div>
                {invoice.dunningError && (
                    <p className="m-0 mt-1 text-[12px] text-[#b45309]">
                        Dunning history refused: {invoice.dunningError}
                    </p>
                )}
            </div>
            <span className="whitespace-nowrap pt-0.5 text-right">
                <span className="font-semibold tabular-nums">
                    {formatAmount(invoice.outstanding)}
                </span>
                {invoice.total !== null &&
                    invoice.outstanding !== null &&
                    invoice.total > invoice.outstanding && (
                        <span className="block text-[11px] text-[#6b7280]">
                            of {formatMoney(invoice.total)}; Troy is told about the{' '}
                            {formatMoney(invoice.total - invoice.outstanding)} already paid
                        </span>
                    )}
            </span>
        </li>
    )
}

const InvoiceGroup = ({ title, invoices }: { title: string; invoices: PlannedInvoice[] }) => {
    if (invoices.length === 0) {
        return null
    }

    return (
        <div className="mb-4 last:mb-0">
            <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]">
                {title} ({invoices.length})
            </h4>
            <ul className="rounded-[6px] border border-[#e5e7eb]">
                {invoices.map((invoice) => (
                    <InvoiceRow key={invoice.invoiceId} invoice={invoice} />
                ))}
            </ul>
        </div>
    )
}

interface TroyCase {
    caseId: string
    invoices: PlannedInvoice[]
    stopped: boolean
}

/** Filed invoices grouped by the Troy case they share, oldest case first. */
const groupByCase = (invoices: PlannedInvoice[]): TroyCase[] => {
    const byCase = new Map<string, PlannedInvoice[]>()
    invoices.forEach((invoice) => {
        if (invoice.troyCaseId === null) {
            return
        }
        byCase.set(invoice.troyCaseId, [...(byCase.get(invoice.troyCaseId) ?? []), invoice])
    })

    return [...byCase.entries()].map(([caseId, caseInvoices]) => ({
        caseId,
        invoices: caseInvoices,
        stopped: caseInvoices.some((invoice) => invoice.troyCollectionStopped !== null),
    }))
}

/**
 * One block per Troy case: the invoices on it, and the actions, which act on the case as a
 * whole because that is the unit Troy stops, resumes and settles.
 */
const CaseGroup = ({ cases, onCaseChanged }: { cases: TroyCase[]; onCaseChanged: () => void }) => {
    if (cases.length === 0) {
        return null
    }

    const invoiceCount = cases.reduce((sum, troyCase) => sum + troyCase.invoices.length, 0)

    return (
        <div className="mb-4 last:mb-0">
            <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6b7280]">
                At Troy ({plural(invoiceCount, 'invoice')} on {plural(cases.length, 'case')})
            </h4>
            <div className="flex flex-col gap-2">
                {cases.map((troyCase) => (
                    <div key={troyCase.caseId} className="rounded-[6px] border border-[#e5e7eb]">
                        <div className="flex flex-wrap items-center gap-2 border-b border-[#f0f1f3] bg-[#f9fafb] px-3 py-1.5 text-[12px] text-[#6b7280]">
                            <Icon name="gavel" fontSize={14} className="inherit" />
                            <span>case {troyCase.caseId}</span>
                            {troyCase.stopped && (
                                <Badge tone="muted" icon="pause_circle">
                                    stopped
                                </Badge>
                            )}
                        </div>
                        <ul>
                            {troyCase.invoices.map((invoice) => (
                                <InvoiceRow
                                    key={invoice.invoiceId}
                                    invoice={invoice}
                                    showCase={false}
                                />
                            ))}
                        </ul>
                        <div className="border-t border-[#f0f1f3] px-3 pb-2">
                            <TroyCaseActions
                                caseId={troyCase.caseId}
                                invoiceId={troyCase.invoices[0].invoiceId}
                                invoiceCount={troyCase.invoices.length}
                                stopped={troyCase.stopped}
                                onChanged={onCaseChanged}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const SendToTroyButton = ({ providerId }: Props) => {
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [invoices, setInvoices] = useState<PlannedInvoice[] | null>(null)
    const [hasRun, setHasRun] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const dispatch = useAppDispatch()

    const loadPreview = async () => {
        setIsLoading(true)

        try {
            const response = await call(`office/invoices/troy-handover-preview/${providerId}`)
            setInvoices(response.invoices)
        } catch (e: unknown) {
            setError((e as { error?: string })?.error ?? 'Could not load the preview')
        } finally {
            setIsLoading(false)
        }
    }

    const openDialog = () => {
        setInvoices(null)
        setHasRun(false)
        setError(null)
        setOpen(true)
        void loadPreview()
    }

    /** A stop or a settlement changed what Troy holds, so the list and the invoice table reload. */
    const onCaseChanged = () => {
        dispatch({ type: 'INVALIDATE_INVOICES' })
        void loadPreview()
    }

    const confirm = async () => {
        setIsLoading(true)
        setError(null)

        try {
            const response = await call(`office/invoices/troy-handover/${providerId}`, {
                json: {},
            })
            setInvoices(response.invoices)
            setHasRun(true)
            dispatch({ type: 'INVALIDATE_INVOICES' })
        } catch (e: unknown) {
            setError((e as { error?: string })?.error ?? 'The handover failed')
        } finally {
            setIsLoading(false)
        }
    }

    const all = invoices ?? []
    const sent = all.filter((invoice) => invoice.handedOver)
    const failed = all.filter((invoice) => invoice.error)
    /** What the next click acts on: eligible and not yet accepted by Troy, so a retry after a
     *  partial failure only re-sends what is actually still missing. */
    const pending = all.filter((invoice) => invoice.eligible && !invoice.handedOver)
    /** Filed before this dialog opened: these carry the stop and settle actions. */
    const atTroy = all.filter(
        (invoice) => invoice.troyCaseId !== null && !invoice.handedOver && !invoice.error,
    )
    const skipped = all.filter(
        (invoice) =>
            !invoice.eligible &&
            !invoice.handedOver &&
            !invoice.error &&
            invoice.troyCaseId === null,
    )
    const pendingTotal = pending.reduce((sum, invoice) => sum + (invoice.outstanding ?? 0), 0)
    const dunningRefused = sent.filter((invoice) => invoice.dunningError)
    const blockedByStoppedCase = all.filter((invoice) => invoice.reason === 'debtor_case_stopped')
    const cases = groupByCase(atTroy)

    /**
     * Reports what happened rather than that something happened. Believing a debt reached
     * Troy when it did not means nobody is chasing it, so a failure must not read as success.
     *
     * Before a run the title names what this dialog is for right now: sending when there is
     * something to send, managing the live cases when there is not. The button outside is
     * state-agnostic ("Manage Troy case"), so this is where the user learns which one it is.
     */
    const outcome = (() => {
        if (!hasRun) {
            if (pending.length > 0) {
                return {
                    title: 'Send open invoices to Troy',
                    icon: 'gavel',
                    tone: 'primary' as Tone,
                }
            }
            if (atTroy.length > 0) {
                return {
                    title: 'Troy cases for this provider',
                    icon: 'gavel',
                    tone: 'primary' as Tone,
                }
            }
            return { title: 'Troy collection', icon: 'gavel', tone: 'primary' as Tone }
        }
        if (sent.length === 0) {
            return { title: 'Nothing was sent to Troy', icon: 'error', tone: 'danger' as Tone }
        }
        if (failed.length > 0) {
            return {
                title: `Sent ${sent.length} to Troy, ${failed.length} failed`,
                icon: 'error',
                tone: 'danger' as Tone,
            }
        }
        return {
            title: `Sent ${plural(sent.length, 'invoice')} to Troy`,
            icon: 'check_circle',
            tone: 'success' as Tone,
        }
    })()

    return (
        <div>
            <Button appearance="secondary" iconBefore="gavel" onClick={openDialog}>
                Manage Troy case
            </Button>
            <Dialog.Root
                open={open}
                onClose={() => setOpen(false)}
                className={`max-h-[85vh] md:w-[640px] md:max-w-full ${DIALOG_BACKDROP_FADE}`}>
                <Dialog.CloseButton onClick={() => setOpen(false)} aria-label="Close" />
                <div className="flex max-h-[85vh] flex-col">
                    <div className="border-b border-[#e5e7eb] px-5 py-4 pr-12">
                        <div className="flex items-center gap-2">
                            <Icon
                                name={outcome.icon}
                                fontSize={22}
                                style={{ color: TONE_COLORS[outcome.tone] }}
                            />
                            <h3 className="m-0">{outcome.title}</h3>
                        </div>
                        <p className="m-0 mt-0.5 text-[13px] text-[#6b7280]">
                            Provider #{providerId}
                        </p>
                    </div>

                    <div
                        className={`flex-1 overflow-y-auto px-5 py-4 ${
                            isLoading && invoices !== null ? 'opacity-50' : ''
                        }`}>
                        {isLoading && invoices === null && (
                            <div className="py-6 text-center text-[#6b7280]">
                                <Loading />
                                <p className="mt-2">Reading open invoices from Payt…</p>
                            </div>
                        )}

                        {error && (
                            <Callout tone="danger" icon="error">
                                {error}
                            </Callout>
                        )}

                        {!hasRun && pending.length > 0 && (
                            <Callout tone="warning" icon="warning">
                                Troy starts collecting immediately. This can only be undone by
                                contacting Troy.
                            </Callout>
                        )}

                        {!hasRun && all.length > 0 && pending.length === 0 && (
                            <Callout tone="muted" icon="info">
                                None of these invoices can be sent to Troy right now. The reason is
                                shown per invoice below.
                            </Callout>
                        )}

                        {hasRun && failed.length > 0 && (
                            <Callout tone="danger" icon="error">
                                {plural(failed.length, 'invoice')} did not reach Troy and{' '}
                                {failed.length === 1 ? 'is' : 'are'} still uncollected. The reason
                                is shown per invoice below.
                            </Callout>
                        )}

                        {hasRun && failed.length === 0 && sent.length > 0 && (
                            <Callout tone="success" icon="check_circle">
                                Troy is now collecting{' '}
                                {sent.length === 1 ? 'this invoice' : 'these invoices'}.
                                {dunningRefused.length === 0 &&
                                    ' The dunning history has been handed over as well.'}
                            </Callout>
                        )}

                        {hasRun && dunningRefused.length > 0 && (
                            <Callout tone="warning" icon="warning">
                                Troy refused the dunning history for{' '}
                                {plural(dunningRefused.length, 'invoice')}. Collection is
                                unaffected, but Troy cannot see what we already sent the debtor.
                                Resending is safe once the refusal is resolved.
                            </Callout>
                        )}

                        {blockedByStoppedCase.length > 0 && (
                            <Callout tone="warning" icon="block">
                                {plural(blockedByStoppedCase.length, 'invoice')} cannot be sent
                                while this provider&apos;s Troy case is stopped. Resume the case
                                below first, or leave it if the debtor has paid everything.
                            </Callout>
                        )}

                        {invoices !== null && invoices.length === 0 && (
                            <div className="py-6 text-center text-[#6b7280]">
                                <Icon name="receipt_long" fontSize={32} grey />
                                <p className="mt-1">This provider has no open invoices.</p>
                            </div>
                        )}

                        <InvoiceGroup title="Sent to Troy" invoices={sent} />
                        <InvoiceGroup title="Failed" invoices={failed} />
                        <InvoiceGroup
                            title={hasRun ? 'Still to send' : 'Will be sent'}
                            invoices={pending}
                        />
                        <CaseGroup cases={cases} onCaseChanged={onCaseChanged} />
                        <InvoiceGroup title="Skipped" invoices={skipped} />
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-[#e5e7eb] px-5 py-3">
                        <span className="text-[13px] text-[#6b7280]">
                            {pending.length > 0
                                ? `${plural(pending.length, 'invoice')} · ${formatMoney(pendingTotal)}`
                                : ''}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                appearance="secondary"
                                disabled={isLoading}
                                onClick={() => setOpen(false)}>
                                {pending.length > 0 && !hasRun ? 'Cancel' : 'Close'}
                            </Button>
                            {pending.length > 0 && (
                                <Button
                                    disabled={isLoading}
                                    appearance="primary"
                                    iconBefore="gavel"
                                    onClick={confirm}>
                                    {(() => {
                                        if (isLoading) {
                                            return 'Sending…'
                                        }
                                        return hasRun
                                            ? `Retry ${pending.length}`
                                            : `Send ${pending.length} to Troy`
                                    })()}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog.Root>
        </div>
    )
}

export default SendToTroyButton
